/**
 * **D272 — the failure vocabulary: which sentence a Failed open earns, in one copy.**
 *
 * R4 §10's non-goal was *"no new resident-facing sentence is added anywhere on this path"*, and it
 * was stated to stop **conditions** being answered by copy — a screen explaining away a state it
 * should have fixed. **D272 amends it for exactly this set and no wider**, because what is added
 * here answers a **reason**, which is the thing `DenyReason` has always been allowed to do.
 *
 * **D223's binary is untouched.** Nothing below invents a third outcome: a Failed stays Failed and
 * gains a cause, exactly as an access refusal already carries one. The Success side of D223 —
 * `acknowledged`, `rejected`, `gate-acting`, and both `409`s — never reaches this map, and the two
 * `DeliveryOutcome` members that mean Success are deliberately absent from
 * {@link RenderedDeliveryFailure} so a screen that routed one here would stop compiling rather than
 * reach for a string. That is `BLE_FAILURE_KEY`'s own construction, and for its own reason.
 *
 * **The rule every one of these sentences is written against is that map's too: a message may only
 * assert what something actually witnessed.** It bites hardest on `timeout`, which is why the
 * sentence is *the gate did not answer* and never *the gate is unpowered*: Q22 established that the
 * controller publishes no retained status and registers no Last-Will, so nothing in the platform
 * can tell an unpowered gate from one whose SIM is down from a `BrokerCode` typed in the wrong
 * case. Zero frames is zero frames.
 *
 * **And PEC-8 still holds on that row, which is why it keeps its check-before-retry clause.** A
 * timeout is not proof the gate did nothing — the command may have actuated and every reply been
 * lost — so the sentence names the silence and stops there. `not-acting` is the one row where the
 * gate itself answered and reported its leaf still, and it is the only one entitled to say the gate
 * did not move.
 */

import type { DeliveryOutcome } from '@selogate/core'
import { NetworkError, RequestTimeoutError, SessionUnavailableError } from '../../auth/transport'

/**
 * The delivery outcomes a resident is given a reason for.
 *
 * `acknowledged`, `rejected` and `gate-acting` are excluded because D223 routes all three to
 * Success; excluding them **from the type** rather than only from the map below is what makes that
 * a compiler rule instead of a convention.
 */
export type RenderedDeliveryFailure = Exclude<DeliveryOutcome, 'acknowledged' | 'rejected' | 'gate-acting'>

/**
 * R6 §2 F2/F3/F4.
 *
 * **These key names exist in BOTH the `home` and `gate` namespaces, and that is the shape the
 * screens already had** — `openFailed`, `openUnconfirmed` and `openNotAuthorized` are each carried
 * twice today, in each namespace's own voice, because a tile's sub-line and a screen's block are
 * not the same width or the same register. So this map returns a **key** and never a string, and
 * each screen resolves it against its own namespace; a single shared namespace would have made one
 * of the two surfaces read wrong to save a duplicate nobody was asking to remove.
 */
export const DELIVERY_FAILURE_KEY = {
  /** F2 — the command said nothing and the appended `STA:*?` said nothing either (D225). */
  timeout: 'openGateUnreachable',
  /** F3 — the gate answered the status query and reported its leaf still. */
  'not-acting': 'openGateNotActing',
  /** F4 — the broker was unreachable, so nothing was published and nothing actuated. */
  unavailable: 'openServiceUnavailable',
} as const satisfies Record<RenderedDeliveryFailure, string>

/**
 * Narrow a wire value to the three the client may draw a sentence from.
 *
 * **`undefined` in is `undefined` out, and that is the whole of D274's optionality reaching the
 * screen.** An absent `deliveryOutcome` means *this server did not say* — a replay off a record
 * written before the field existed, or a bundle deployed before it — and the honest answer there is
 * the sentence the product already had. It is never read as `'timeout'`.
 */
export function renderedDeliveryFailure(
  outcome: DeliveryOutcome | undefined,
): RenderedDeliveryFailure | undefined {
  return outcome !== undefined && outcome in DELIVERY_FAILURE_KEY
    ? (outcome as RenderedDeliveryFailure)
    : undefined
}

/**
 * The sentence a Failed cloud open renders, with today's copy as the answer when nothing is known.
 *
 * Kept as one function rather than inlined at the two screens because a fork here is two screens
 * naming one cause differently, which is the divergence `decideFallback` was consolidated to
 * remove one subsystem over.
 */
export type OpenFailureKey =
  'openUnconfirmed' | (typeof DELIVERY_FAILURE_KEY)[RenderedDeliveryFailure] | 'openGateClosing'

export function deliveryFailureKey(failure: RenderedDeliveryFailure | undefined): OpenFailureKey {
  return failure === undefined ? 'openUnconfirmed' : DELIVERY_FAILURE_KEY[failure]
}

/**
 * **D294's refusal — the one a Failed open can earn that is not about the delivery at all, and the
 * reason it is a SENTENCE rather than a cause.**
 *
 * Every other row in this file answers *what happened to the command*; `gate-closing` answers *which
 * way the gate is travelling*, which is why it cannot ride {@link CONTENDED_CAUSE}: that map's values
 * are `DeliveryOutcome` members, and a close holding the lease is not one. Mapping it to `undefined`
 * there would have rendered the generic *sent, unconfirmed* line over a command that was refused
 * before anything was published — the false-Success shape D294 exists to remove, one register down.
 *
 * **It is a new sentence, and R4 §10's non-goal permits exactly this.** D272 amended that non-goal
 * for sentences answering a *reason*; *the gate is closing* is a reason, and it is the only thing the
 * resident needs in order to decide whether to wait or to tap again. The two existing F2/F3 rows
 * cannot carry it — *the gate did not answer* and *the gate did not move* are both false here, the
 * gate being in motion and the platform never having asked it anything.
 *
 * **Two screens, two registers, one key name** — `DELIVERY_FAILURE_KEY`'s own rule: the key lives in
 * both the `home` and the `gate` namespace and each screen resolves it against its own.
 */
export function openFailureKey(input: {
  /**
   * **Explicit `undefined` is accepted, on {@link closeFailureKey}'s rule rather than
   * {@link deliveryFailurePatch}'s.** This is an argument and not a patch: `undefined` means *no
   * refusal of this kind*, which has its own answer — whatever the delivery said.
   */
  readonly refusal?: 'gate-closing' | undefined
  readonly failure?: RenderedDeliveryFailure | undefined
}): OpenFailureKey {
  if (input.refusal === 'gate-closing') return 'openGateClosing'
  return deliveryFailureKey(input.failure)
}

/**
 * The cause as a **patch**, because `exactOptionalPropertyTypes` is on.
 *
 * An explicit `deliveryFailure: undefined` is a different thing from an absent one and does not
 * assign; spreading `{}` is what says *the server did not say*, which is the shape `position` and
 * every other conditional field on both screens already uses. It lives here rather than twice
 * because two screens narrowing one wire field for themselves is how they come to disagree.
 */
export function deliveryFailurePatch(outcome: DeliveryOutcome | undefined): {
  readonly deliveryFailure?: RenderedDeliveryFailure
} {
  const deliveryFailure = renderedDeliveryFailure(outcome)
  return deliveryFailure === undefined ? {} : { deliveryFailure }
}

/*
 * ---------------------------------------------------------------------------
 * D297 — the four answers a CONTENDED open earns, and the one of them that is Success.
 * ---------------------------------------------------------------------------
 */

/**
 * **The three codes a contended open is Failed by, and the reason there is no fourth string.**
 *
 * D223 answered `409 gate-busy` with Success on the argument that *the platform has the command*,
 * and its own text flagged that clause as asserting rather than observing. Production disconfirmed
 * it: on 2026-09-09 the refusal landed 3.4 s into a holder's 4.0 s exchange that ended
 * `outcome: timeout`, and the resident saw a green tick over a gate that never moved. The server now
 * waits for the holder's exchange and names what the gate said.
 *
 * **`gate-busy` is deliberately NOT a member.** It keeps meaning Success — the gate is acting, or a
 * command really is in flight — so a bundle deployed before this decision renders both of those
 * exactly as it did, and the three below are codes it has never seen and falls through to its
 * generic failure on, which is the Failed side.
 *
 * **Each maps onto one of D272's EXISTING sentences and mints none.** `gate-unreachable` is F2's
 * *the gate did not answer*, `gate-not-acting` is F3's *the gate answered and did not start
 * moving* — the one row entitled to say the leaf did not move — and `gate-command-failed` is the
 * honest *nothing more is known*, which earns the line the product already had. The resident's
 * question (*is this gate going to open?*) has the same answer whoever asked it, so re-voicing
 * these for a contended tap would be three keys in five catalogs that agree until one is
 * retranslated (D255).
 */
export type ContendedOpenRefusal = 'gate-unreachable' | 'gate-not-acting' | 'gate-command-failed'

/**
 * The same three, as the set `api.ts` narrows a wire `code` against.
 *
 * It is `satisfies` a record over the type rather than a hand-written array, so a member added to
 * the union without a row here stops compiling — `DELIVERY_FAILURE_KEY`'s own construction, and the
 * reason the set and the type cannot drift.
 */
export const CONTENDED_OPEN_REFUSALS = [
  'gate-unreachable',
  'gate-not-acting',
  'gate-command-failed',
] as const satisfies readonly ContendedOpenRefusal[]

/**
 * **The cause each contended refusal carries, expressed in D272's own vocabulary.**
 *
 * The two the gate's own row witnessed map onto the `DeliveryOutcome` members that mean the same
 * thing, so both screens reach {@link deliveryFailureKey} and every existing sentence, layout rule
 * and line count applies unchanged. `gate-command-failed` maps to `undefined`, which is D274's
 * *this server did not say* — an orphaned lease or a holder that wrote nothing — and **that is the
 * right sentence rather than a shortfall**: PEC-8 says a command that was published and lost is not
 * proof the gate did nothing, so *the command was sent, the gate did not confirm, check the gate*
 * is what a crashed holder leaves behind.
 */
const CONTENDED_CAUSE = {
  'gate-unreachable': 'timeout',
  'gate-not-acting': 'not-acting',
  'gate-command-failed': undefined,
} as const satisfies Record<ContendedOpenRefusal, RenderedDeliveryFailure | undefined>

/**
 * Whether a wire refusal is one of the three, so a screen can branch on it without restating the
 * set. **`gate-busy` answers `false`** — it is Success, and that is the whole of D223's binary
 * surviving this decision.
 */
export function isContendedOpenRefusal(reason: string | undefined): reason is ContendedOpenRefusal {
  return reason !== undefined && reason in CONTENDED_CAUSE
}

/**
 * The cause as a **patch**, on {@link deliveryFailurePatch}'s own `exactOptionalPropertyTypes` rule:
 * an explicit `deliveryFailure: undefined` is a different thing from an absent one and does not
 * assign, so spreading `{}` is what says *the server did not say*.
 */
export function contendedFailurePatch(reason: ContendedOpenRefusal): {
  readonly deliveryFailure?: RenderedDeliveryFailure
} {
  const cause = CONTENDED_CAUSE[reason]
  return cause === undefined ? {} : { deliveryFailure: cause }
}

/**
 * **D272 F1 — *the phone has no data*, and the two subclasses that must not answer to it.**
 *
 * `RequestTimeoutError` **and** `SessionUnavailableError` both `extend NetworkError`, so the
 * obvious `error instanceof NetworkError` is true for all three — and it shipped that way for one
 * test run. Each of the two is a false claim in its own direction. A `RequestTimeoutError` ran the
 * full `REQUEST_TIMEOUT_MS` budget, which D189(b) reads as *the request **was** sent*: telling that
 * resident their phone has no connection names a state nothing witnessed, and points them at the
 * one thing that is working. A `SessionUnavailableError` is the session service answering that it
 * is unavailable — an answer, from a network that carried it.
 *
 * **So this is a positive test for the base class alone**, and it is a function rather than an
 * inline `instanceof` at two screens because the trap is invisible at the call site: the naive
 * form reads correct, compiles, and is wrong on two of three inputs. `BLE_FAILURE_KEY`'s rule
 * decides it — a message may only assert what something actually witnessed.
 */
export function isNoDataFailure(error: unknown): boolean {
  return (
    error instanceof NetworkError &&
    !(error instanceof RequestTimeoutError) &&
    !(error instanceof SessionUnavailableError)
  )
}

/*
 * ---------------------------------------------------------------------------
 * R7/D277 — the CLOSE's half of this vocabulary, and the reason it is a second map.
 * ---------------------------------------------------------------------------
 */

/**
 * **The delivery outcomes a resident is given a reason for after a CLOSE — and it is one member
 * wider than the open's, which is the whole point.**
 *
 * `rejected` is excluded from {@link RenderedDeliveryFailure} because D223 routes an open's
 * `CMD:Err` to **Success**: it means *already doing it* (D216), so the gate has the command.
 * **R7 S1 measured that premise false for a close.** C3: a bare Close sent to a moving leaf
 * **stops** it, so a refusal is not evidence the gate is closing. C6 (3/3): a Close onto a
 * *closing* leaf is **ignored, not refused**, and `CMD:Err` was never observed on a Close at all,
 * 0 across every arm. Carrying the Success mapping across would carry a refuted argument.
 *
 * So a close has one Success set (`acknowledged`, `gate-acting`) and the rest earn a sentence.
 * **Two types rather than a parameter on one**, for D277 (a)'s reason one layer down: a direction
 * argument is what lets a call site pass the wrong one, and here the wrong one is silent — it
 * would render a refusal as a success on exactly the command that moves a leaf toward a person.
 */
export type RenderedCloseFailure = Exclude<DeliveryOutcome, 'acknowledged' | 'gate-acting'>

/**
 * The close's sentences, keyed the same way and for the same reason — the screens resolve each
 * against their own namespace.
 *
 * **The three shared rows say something different from the open's and are not reused**, because
 * a resident asked for a close and *the gate did not open* is not the sentence. `not-acting` is
 * the one row entitled to say the leaf did not move: there the gate answered the appended
 * `STA:*?` and reported its own position. `timeout` names the silence and stops, on Q22's ground
 * that nothing can tell an unpowered gate from a downed SIM — and **PEC-8's check-before-retry
 * clause matters more here than on the open path**, because a Close that actuated and lost every
 * reply left a leaf travelling toward whatever is in the way.
 */
export const CLOSE_FAILURE_KEY = {
  /** The command said nothing and the appended `STA:*?` said nothing either. */
  timeout: 'closeGateUnreachable',
  /** The gate answered the status query and reported its leaf still — or still opening. */
  'not-acting': 'closeGateNotActing',
  /** The broker was unreachable, so nothing was published and nothing actuated. */
  unavailable: 'closeServiceUnavailable',
  /**
   * **`CMD:Err`, and the row that does not exist on the open side.** The controller answered and
   * refused. On this evidence the leaf's state is what it was, so the sentence says the gate
   * would not take the command rather than that it did not move.
   */
  rejected: 'closeRefused',
} as const satisfies Record<RenderedCloseFailure, string>

/**
 * Narrow a wire value to the four a close may draw a sentence from.
 *
 * `undefined` in is `undefined` out — D274's optionality reaching the screen, unchanged: an absent
 * `deliveryOutcome` means *this server did not say*, never `'timeout'`.
 */
export function renderedCloseFailure(outcome: DeliveryOutcome | undefined): RenderedCloseFailure | undefined {
  return outcome !== undefined && outcome in CLOSE_FAILURE_KEY ? (outcome as RenderedCloseFailure) : undefined
}

export type CloseFailureKey =
  | 'closeUnconfirmed'
  | (typeof CLOSE_FAILURE_KEY)[RenderedCloseFailure]
  | 'closeGateBusy'
  | 'closeNotSupported'

/**
 * The sentence a Failed close renders, with today's generic line when nothing is known.
 *
 * **`gate-busy` is handled here and not by the open's refusal path, and that is D277 (b)'s first
 * inversion.** D223 routes `409 gate-busy` to **Success** for an open, on the argument that the
 * lease holder's command *is* the gate opening — the resident's goal is met by somebody else's
 * tap. For a close **the holder is someone opening the gate**, so the goal is the exact opposite
 * of met, and the sentence names the cause rather than the generic failure.
 *
 * `close-not-supported` is here for completeness and a resident should never see it: the control is
 * drawn only where the gate reads `command`. It exists because the mode can change under an open
 * Gate Detail (D277 (f)) — a client is not the safety layer — and a refusal with no sentence is
 * how a correct server answer reaches a screen as a dead button.
 */
export function closeFailureKey(input: {
  readonly refusal?: 'gate-busy' | 'close-not-supported' | undefined
  /**
   * **Explicit `undefined` is accepted here, and that is the opposite of
   * {@link deliveryFailurePatch}'s rule for a reason.** That function builds a *patch* for a state
   * object under `exactOptionalPropertyTypes`, where an explicit `undefined` and an absent key are
   * different things and only absence means *do not set this*. This is an *argument*, and here
   * `undefined` is a meaningful value with its own answer — *the server did not say*, which earns
   * today's generic line. Forcing callers to spread a conditional to express it would make the one
   * input that has a documented meaning the hardest one to pass.
   */
  readonly failure?: RenderedCloseFailure | undefined
}): CloseFailureKey {
  if (input.refusal === 'gate-busy') return 'closeGateBusy'
  if (input.refusal === 'close-not-supported') return 'closeNotSupported'
  return input.failure === undefined ? 'closeUnconfirmed' : CLOSE_FAILURE_KEY[input.failure]
}

/** The close's cause as a patch, on {@link deliveryFailurePatch}'s own `exactOptionalPropertyTypes` rule. */
export function closeFailurePatch(outcome: DeliveryOutcome | undefined): {
  readonly closeFailure?: RenderedCloseFailure
} {
  const closeFailure = renderedCloseFailure(outcome)
  return closeFailure === undefined ? {} : { closeFailure }
}
